import React, { useCallback, useLayoutEffect } from 'react';

import {
  LoadingAnimation,
  NotFoundView,
  SignedOutView,
} from '@order/components';
import { useCustomer } from '@order/Customer';
import { useLocalizationContext } from '@order/Localization';
import { usePromoCode } from '@order/PromoCode';
import { useTelemetry } from '@order/Telemetry';

import { FutureDateModal } from '../../components/FutureDateModal';
import { RewardExceptionModal } from '../../components/RewardExceptionModal';
import { useCart, useNewException } from '../../hooks';
import { useNavigateToMainTabs } from '../../navigation/AppNavigation.hooks';
import type { CheckoutScreenProps } from '../../navigation/AppNavigation.props';
import {
  CheckoutAddressForm,
  CheckoutDetailsBlock,
  CheckoutExtrasBlock,
  CheckoutHeader,
  CheckoutLayout,
  CheckoutLayoutColumn,
  CheckoutOrderBlock,
  CheckoutPaymentBlock,
  CheckoutWrapper,
} from './components';
import { CheckoutTipBlock } from './components/Blocks/CheckoutTipBlock';
import { useAvailableWantedTimes } from './hooks/useAvailableWantedTime';
import { useCheckoutData } from './hooks/useCheckoutData';
import { useCheckoutHandlers } from './hooks/useCheckoutHandlers';
import { useFutureDateModal } from './hooks/useFutureDateModal';

export const CheckoutScreen = ({ navigation }: CheckoutScreenProps) => {
  const { customer } = useCustomer();
  const { t } = useLocalizationContext();
  const navigateToMainTabs = useNavigateToMainTabs(navigation);

  const handleAddNewCardSelected = useCallback(() => {
    navigation.navigate('PaymentMethodAdd');
  }, [navigation]);

  const replaceWithOrderStatus = useCallback(
    (orderId: string) => {
      navigation.replace('OrderStatus', { orderId });
    },
    [navigation],
  );

  const dismissAndNavigateToLocations = useCallback(() => {
    void navigateToMainTabs('MenuTab', { screen: 'Location' });
  }, [navigateToMainTabs]);

  // ───── Wanted Times ─────────────────────────────────────────────
  const {
    availableWantedTimes,
    loadingAvailableWantedTimes,
    handleInvalidWantedTime,
  } = useAvailableWantedTimes();

  // ───── Future Date Modal ────────────────────────────────────────
  const {
    shouldShowFutureDateModal,
    setShouldShowFutureDateModal,
    handleDismissFutureModal,
  } = useFutureDateModal();

  // ───── Checkout Data ────────────────────────────────────────────
  const {
    isCustomerValid,
    isCartValid,
    isLoggedOut,
    isLoadingCheckout,
    checkoutCredit,
    availableCredit,
    deliveryTip,
    creditCardOptions,
    hasAddress,
    payload,
    submitting,
    submittingAddress,
    canSubmitOrder,
    shouldShowErrorNotice,
    validateRewardException,
    orderAddress,
    checkoutViewEvent,
    checkoutFieldsValidation,
    refetchCredits,
    submitOrder,
    setPayload,
    handleUpdateAddress,
    resetCheckoutFieldValidation,
  } = useCheckoutData({
    replaceWithOrderStatus,
    handleInvalidWantedTime,
  });

  // ───── Checkout Handlers ────────────────────────────────────────
  const handlers = useCheckoutHandlers({
    setPayload,
    handleAddNewCardSelected,
    setShouldShowFutureDateModal,
  });

  // ───── Promo Code ───────────────────────────────────────────────
  const {
    promoCode,
    isSubmittingPromoCode,
    promoCodeInvalid: hasPromoCodeError,
    setPromoCode,
    submitPromoCode: handlePromoCodeSubmit,
  } = usePromoCode({ refetchCredits });

  // ───── Cart Data ────────────────────────────────────────────────
  const { cart, cartOrderType } = useCart();
  const {
    ledger,
    lineItems,
    restaurant,
    deliveryOrderDetail,
    id: orderId,
  } = cart ?? {};

  const title = t('checkout.finish-order', { orderType: cartOrderType });

  const {
    exceptionMessage: validateRewardExceptionMessage,
    hasNewException: shouldShowValidateRewardExceptionModal,
  } = useNewException(validateRewardException);

  const handlePressCreditAdd = useCallback(() => {
    navigation.navigate('CreditAdd');
  }, [navigation]);

  useLayoutEffect(() => {
    navigation.setOptions({ title });
  }, [navigation, title]);

  // ───── Telemetry ────────────────────────────────────────────────
  const { trackEvent } = useTelemetry();

  useLayoutEffect(() => {
    if (isCustomerValid && !isLoadingCheckout && checkoutViewEvent.current) {
      trackEvent(checkoutViewEvent.current);
    }
  }, [checkoutViewEvent, isCustomerValid, isLoadingCheckout, trackEvent]);

  // ───── Loading ──────────────────────────────────────────────────
  if (isLoadingCheckout) return <LoadingAnimation />;

  // ───── Logged Out -> Login ──────────────────────────────────────
  if (isLoggedOut) {
    return <SignedOutView />;
  }

  // ───── Render Control ───────────────────────────────────────────
  if (!isCustomerValid) return <LoadingAnimation />;

  // ───── Empty Cart -> Location ───────────────────────────────────
  if (!isCartValid) {
    return (
      <NotFoundView
        btnLabel={t('not-found-view.empty-cart')}
        description={t('bag.empty')}
        onBtnPress={dismissAndNavigateToLocations}
      />
    );
  }

  const requiresDeliveryAddress = cartOrderType === 'delivery' && !hasAddress;

  if (requiresDeliveryAddress) {
    return <CheckoutAddressForm orderAddress={orderAddress ?? undefined} />;
  }

  return (
    <CheckoutWrapper>
      <CheckoutHeader orderType={cartOrderType} />

      <CheckoutLayout>
        <CheckoutLayoutColumn>
          <CheckoutDetailsBlock
            orderId={orderId}
            orderType={cartOrderType}
            customer={customer}
            orderAddress={orderAddress ?? undefined}
            availableWantedTimeOptions={availableWantedTimes}
            loadingAvailableWantedTimes={loadingAvailableWantedTimes}
            selectedTime={payload.wantedTime}
            restaurant={restaurant}
            deliveryOrderDetail={deliveryOrderDetail}
            submittingAddress={submittingAddress}
            dropoffLocation={payload.dropoffLocationId}
            handleUpdateAddress={handleUpdateAddress}
            setSelectedTime={handlers.handleWantedTimeSelected}
            setDropoffLocation={handlers.handleDropoffLocationSelected}
            resetCheckoutFieldValidation={resetCheckoutFieldValidation}
            checkoutFieldsValidation={checkoutFieldsValidation}
          />
          <CheckoutPaymentBlock
            creditCardOptions={creditCardOptions}
            credit={checkoutCredit}
            availableCredit={availableCredit}
            useCredit={payload.useCredit}
            billingAccountId={payload.billingAccountId}
            promoCode={promoCode}
            isSubmittingPromoCode={isSubmittingPromoCode}
            hasPromoCodeError={hasPromoCodeError}
            onCreditCardSelected={handlers.handleCreditCardSelected}
            onAddCardSelected={handlers.handleAddNewCardSelected}
            onAvailableCreditSelected={handlers.handleAvailableCreditSelected}
            setPromoCode={setPromoCode}
            onPromoCodeSubmit={handlePromoCodeSubmit}
            onPressCreditAdd={handlePressCreditAdd}
            resetCheckoutFieldValidation={resetCheckoutFieldValidation}
            checkoutFieldsValidation={checkoutFieldsValidation}
          />
          <CheckoutTipBlock
            isDelivery={cartOrderType === 'delivery'}
            subtotal={ledger?.subtotal ?? 0}
            setTip={handlers.handleTip}
          />
          <CheckoutExtrasBlock
            orderType={cartOrderType}
            onIncludeUtensilsToggle={handlers.handleIncludeUtensilsToggle}
            includeUtensils={payload.includeUtensils}
          />
        </CheckoutLayoutColumn>

        <CheckoutLayoutColumn>
          <CheckoutOrderBlock
            lineItems={lineItems}
            ledger={ledger}
            onSubmitOrder={submitOrder}
            submitting={submitting}
            isDelivery={cartOrderType === 'delivery'}
            credit={payload.useCredit ? checkoutCredit : 0}
            tip={deliveryTip}
            isSubmitActive={shouldShowErrorNotice ? true : canSubmitOrder}
          />
        </CheckoutLayoutColumn>
      </CheckoutLayout>

      <RewardExceptionModal
        visible={shouldShowValidateRewardExceptionModal}
        message={validateRewardExceptionMessage}
      />
      <FutureDateModal
        visible={shouldShowFutureDateModal}
        onConfirm={handleDismissFutureModal}
      />
    </CheckoutWrapper>
  );
};
